import { backendClient } from "@/api/backendClient";

import type {
  MaintenanceStrategy,
  MaintenanceStrategyExcludedDateInsert,
  MaintenanceStrategyExtended,
  MaintenanceStrategyInsert,
  MaintenanceStrategyListQueryParams,
  MaintenanceStrategySetActive,
  MaintenanceStrategyUpdate,
} from "@waku-care/types/contracts/internal";

export const getMaintenanceStrategy = async (id: string) => {
  const { data, error } = await backendClient.get<MaintenanceStrategyExtended>(`/maintenance-strategies/${id}`);
  if (error) throw error;
  return data;
};

export interface ListMaintenanceStrategiesByWorkspaceIdProps {
  workspace_id?: string;
  params?: MaintenanceStrategyListQueryParams;
}
// workspace incl. its child workspaces; no `limit` returns all matching strategies
export const getMaintenanceStrategiesByWorkspaceId = async ({
  workspace_id,
  params,
}: {
  workspace_id: string;
  params?: MaintenanceStrategyListQueryParams;
}) => {
  const { data, count, error } = await backendClient.get<MaintenanceStrategyExtended[]>(
    `/maintenance-strategies/by-workspace/${workspace_id}`,
    { params },
  );
  if (error) throw error;
  return { data, count };
};

// workspace_id is derived from the deployment, device_ids are linked in the same transaction
export const createMaintenanceStrategy = async ({ values }: { values: MaintenanceStrategyInsert }) => {
  const { data, error } = await backendClient.post<MaintenanceStrategyExtended>(`/maintenance-strategies`, {
    body: values,
  });
  if (error) throw error;
  return data;
};

// deployment_id can only be changed while no devices are linked (409 otherwise)
export const updateMaintenanceStrategy = async ({ id, values }: { id: string; values: MaintenanceStrategyUpdate }) => {
  const { data, error } = await backendClient.patch<MaintenanceStrategyExtended>(`/maintenance-strategies/${id}`, {
    body: values,
  });
  if (error) throw error;
  return data;
};

export const deleteMaintenanceStrategy = async (id: string) => {
  const { error } = await backendClient.delete<null>(`/maintenance-strategies/${id}`);
  if (error) throw error;
};

// appends one date to excluded_dates (deduplicated by UTC day on the backend)
export const addMaintenanceStrategyExcludedDate = async ({
  id,
  values,
}: {
  id: string;
  values: MaintenanceStrategyExcludedDateInsert;
}) => {
  const { data, error } = await backendClient.post<MaintenanceStrategyExtended>(
    `/maintenance-strategies/${id}/excluded-dates`,
    { body: values },
  );
  if (error) throw error;
  return data;
};

// pause / resume
export const setMaintenanceStrategyActive = async ({
  id,
  values,
}: {
  id: string;
  values: MaintenanceStrategySetActive;
}) => {
  const { data, error } = await backendClient.patch<MaintenanceStrategy>(`/maintenance-strategies/${id}/active`, {
    body: values,
  });
  if (error) throw error;
  return data;
};
